import { useEffect, useState } from "react";
import { MessageCircle, Mail, Phone } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/WhatsAppIcon";
import { useTranslation } from "react-i18next";
import { useSupport } from "@/hooks/useSupport";
import { isIntercomReady } from "@/lib/intercom";
import { track } from "@/lib/analytics";

type HelpContactSurface =
  | "help_landing"
  | "topic_page"
  | "article_page"
  | "search_page"
  | "contact_page";

interface HelpContactCtaProps {
  /** The page surface for analytics. */
  surface: HelpContactSurface;
  /** Optional context (article/topic slug) attached to the Intercom conversation. */
  context?: Record<string, string>;
}

/**
 * Bottom-of-page CTA card asking the user to contact support if the
 * articles didn't answer their question. Wired to the same `useSupport`
 * hook the rest of the app uses (Intercom Messenger with mailto fallback).
 */
export function HelpContactCta({ surface, context }: HelpContactCtaProps) {
  const { t } = useTranslation();
  const support = useSupport();

  // Only offer chat once Messenger is actually ready; direct contact channels
  // below remain available even when it never loads.
  const [intercomReady, setIntercomReady] = useState(() => isIntercomReady());
  useEffect(() => {
    if (intercomReady) return;
    let cancelled = false;
    const start = Date.now();
    const id = window.setInterval(() => {
      if (cancelled) return;
      if (isIntercomReady()) {
        setIntercomReady(true);
        window.clearInterval(id);
      } else if (Date.now() - start > 10_000) {
        window.clearInterval(id);
      }
    }, 500);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [intercomReady]);

  return (
    <section
      aria-labelledby="help-contact-heading"
      className="mt-12 min-w-0 rounded-2xl border border-border bg-gradient-to-br from-primary/5 to-primary/[0.02] p-5 text-center sm:p-8"
      data-testid="help-contact-cta"
    >
      <h2 id="help-contact-heading" className="text-xl font-bold text-foreground sm:text-2xl">
        {t("helpCentre.cta.title", { defaultValue: "Still need help?" })}
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground sm:text-base">
        {t("helpCentre.cta.description", {
          defaultValue: "Our support team is on hand 24/7. Start a chat, and a real human will pick it up.",
        })}
      </p>
      {/* Keep primary channels and labelled phone numbers on separate rows. */}
      <div className="mt-6 flex min-w-0 flex-col flex-wrap items-center gap-3" data-testid="help-contact-actions">
        <div className="flex w-full min-w-0 flex-wrap items-center justify-center gap-3" data-testid="help-contact-primary-actions">
        {/* Never show a chat control that cannot open Messenger. */}
        {intercomReady && (
          <button
            type="button"
            onClick={() => {
              track("help_intercom_cta_click", {
                surface,
                channel: "chat",
                ...(context?.article_slug ? { article_slug: context.article_slug } : {}),
              });
              support.openChat({
                eventName: `${surface}_intercom_cta_click`,
                context: { surface, ...(context || {}) },
              });
            }}
            className="primary-action inline-flex min-h-11 max-w-full min-w-0 items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold"
            data-testid="help-cta-chat"
          >
            <MessageCircle className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 break-words">{t("helpCentre.cta.chat_button", { defaultValue: "Chat with support" })}</span>
          </button>
        )}
        <a
          href={`mailto:${support.email}`}
          onClick={() =>
            track("help_intercom_cta_click", {
              surface,
              channel: "email",
              ...(context?.article_slug ? { article_slug: context.article_slug } : {}),
            })
          }
          className="inline-flex min-h-11 max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-border bg-white px-5 py-3 text-sm font-semibold text-foreground hover:border-primary hover:text-primary"
          data-testid="help-cta-email"
        >
          <Mail className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 break-words">{t("auth.email", { defaultValue: "Email" })}</span>
        </a>
        <a
          href={support.whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            track("help_intercom_cta_click", {
              surface,
              channel: "whatsapp",
              ...(context?.article_slug ? { article_slug: context.article_slug } : {}),
            })
          }
          className="inline-flex min-h-11 max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-border bg-white px-5 py-3 text-sm font-semibold text-foreground hover:border-primary hover:text-primary"
          data-testid="help-cta-whatsapp"
        >
          <span className="shrink-0"><WhatsAppIcon className="h-4 w-4" /></span>
          <span className="min-w-0 break-words">{t("helpCentre.cta.whatsapp_button", { defaultValue: "WhatsApp" })}</span>
        </a>
        </div>
        <div className="flex w-full min-w-0 flex-wrap items-center justify-center gap-3" data-testid="help-contact-phone-actions">
          {support.phoneNumbers.map((phone) => {
            const countryLabel = t(
              phone.region === "uk"
                ? "helpCentre.contact.phone_uk_country"
                : "helpCentre.contact.phone_us_country",
              {
                defaultValue:
                  phone.region === "uk" ? "United Kingdom" : "United States and Canada",
              },
            );
            return (
              <a
                key={phone.region}
                href={`tel:${phone.e164}`}
                onClick={() => track("help_intercom_cta_click", { surface, channel: "phone", ...(context?.article_slug ? { article_slug: context.article_slug } : {}) })}
                className="inline-flex min-h-11 max-w-full min-w-0 items-center justify-center gap-2 rounded-full border border-border bg-white px-5 py-2.5 text-sm font-semibold text-foreground hover:border-primary hover:text-primary"
                data-testid={`help-cta-phone-${phone.region}`}
              >
                <Phone className="h-4 w-4 shrink-0" aria-hidden />
                <span className="flex min-w-0 flex-col items-start leading-tight">
                  <span className="text-xs text-muted-foreground">{countryLabel} · {t("helpCentre.contact.toll_free", { defaultValue: "Toll free" })}</span>
                  <span className="whitespace-nowrap tabular-nums">{phone.flag} {phone.display}</span>
                </span>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
